import React, { useState, useEffect } from 'react'
import useTitle from "../../../hooks/useTitle"
import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
  } from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight2 from "../../../utils/EtContentEditorLight2"
import EtBtnFormBasic from "../../../components/common/buttons/EtBtnFormBasic"
import { useNavigate } from "react-router"

import { useCreateNewUserSuccessorMutation } from '@/features/userSuccessors/userSuccessorsApiSlice'

const EtCreateUserImprintLegacySuccessorComp = ({
    userId, 
    userOrgId, 
    triggeringUrl, 
    onClose
  }) => {

  // First Created - 02 Jan 2025
  // Author - Atul Pandey
  // Inherited from the EtCreateUserImprintCreationComp component
  

  // 0 - Set Page title & variables
  useTitle('User Imprint Legacy - Successor - Create')

  // 1 - set page navigations
  const navigate = useNavigate()
  
  // 2a - set the mutation to create the new user imprint hiddenMagic data
  const [createNewUserSuccessor, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserSuccessorMutation()

  // 3 - Initialize Form Data State
    const [formData, setFormData] = useState({
      userSuccessorName: "",
      userSuccessorDesc: "",
      userSuccessorStartDate: "",
      userSuccessorEndDate: "",
      userSuccessorNotes:[],
      pic: "",
      active: true, // Default active state
    });
  
    // 4 - State variables for the editor
    const [editorKey, setEditorKey] = useState('hiddenMagicDesc-' + Date.now());
    const [editorValue, setEditorValue] = useState('');
    const [collapsedSections, setCollapsedSections] = useState({});
  
    // 5a - Convert Image to Base64
    const convertPicToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
  
    // 5b - Handle Form Changes
    const handleChange = (e) => {
      const { name, value, files } = e.target;
      if (name === "pic") {
        setFormData((prevState) => ({ ...prevState, pic: files[0] }));
      } else {
        setFormData((prevState) => ({ ...prevState, [name]: value }));
      }
    };
  
    // 5c - Toggle show / collapse each section 
    const toggleSection = (key) => {
      setCollapsedSections((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };  

    // 6 - Handle form submission
    const handleSubmit = async (e) => {
      e.preventDefault();

      try {
        const picBase64 = formData.pic
          ? await convertPicToBase64(formData.pic)
          : undefined;

        const validNotes = formData.userSuccessorNotes.filter(
          (note) => note.successorNoteText.trim()
        );

        const payload = {
          userId,
          userOrgId,
          userSuccessorName: formData.userSuccessorName.trim() || undefined,
          userSuccessorDesc: formData.userSuccessorDesc.trim() || undefined,
          userSuccessorStartDate: formData.userSuccessorStartDate || undefined,
          userSuccessorEndDate: formData.userSuccessorEndDate || undefined,
          userSuccessorNotes: validNotes.map((note) => ({
            ...note,
            successorNoteText: note.successorNoteText.trim(),
          })),
          pic: picBase64 || undefined,
          active: formData.active,
        };

        await createNewUserSuccessor(payload).unwrap();

        if (onClose) {
          onClose();
        } else {
          navigate(triggeringUrl || "/userMagicHome");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      }
     
    };

    // 7 - Reset Form on Successful Submission
      useEffect(() => {
        if (isSuccess) {
          setFormData({
            userSuccessorName: "",
            userSuccessorDesc: "",
            userSuccessorStartDate: "",
            userSuccessorEndDate: "",
            userSuccessorNotes:[],
            pic: "",
            active: true, // Default active state
          });
    
          if (onClose) {
            onClose();
          } else {
            navigate(triggeringUrl || "/userMagicHome");
          }
        }
      }, [isSuccess, navigate]);
    
    // 8 - Handle Clear
    const handleClear = () => {
    setFormData({
      userSuccessorName: "",
      userSuccessorDesc: "",
      userSuccessorStartDate: "",
      userSuccessorEndDate: "",
      userSuccessorNotes:[],
      pic: "",
      active: true, // Default active state
    });
    };

    // 9 - Handle Cancel
    const handleCancel = () => {
    if (onClose) {
        onClose();
    } else {
        navigate(triggeringUrl || "/userMagicHome");
    }
    };


  return (
    <div className="mx-auto w-full m-1 p-1 bg-white rounded shadow-md shadow-black">
      {isLoading && <PulseLoader color={'#ea580c'} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">{error?.data?.message}</p>
      )}

      <p className="p-1 m-1 font-bold font-poppins text-xl">
        Create User Imprint Legacy - Successor
      </p>

      <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
        
        {/* 1. Legacy - Successor -  Name */}
        <div id="successorName" className="bg-gray-100 rounded p-1 m-1">
          <label htmlFor="userSuccessorName" className="block text-gray-600 text-sm font-bold mb-1">
            Successor Name
          </label>
          <input
            type="text"
            name="userSuccessorName"
            value={formData.userSuccessorName}
            onChange={handleChange}
            placeholder="Enter successor name"
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* 2. Legacy - Successor -  Desc */}
        <div id="successorDesc" className="bg-gray-100 rounded p-1 m-1">
          <label
            htmlFor="userSuccessorDesc"
            className="block text-gray-600 text-sm font-bold mb-1"
          >
            Successor Description
          </label>
          <EtContentEditorLight2
            value={formData.userSuccessorDesc || ""}
            handleChange={(content) => {
              setFormData((prevState) => ({
                ...prevState,
                userSuccessorDesc: content,
              }));
            }}
            placeholderText="Write your successor description here..."
            editorKey={editorKey}
          />
        </div>

        <div className='flex justify-between items-center'>    
          {/* 3. Legacy - Successor - Start Date */}
          <div id="successorStartDate" className="bg-gray-100 rounded p-1 m-1 w-full">
            <label
              htmlFor="userSuccessorStartDate"
              className="block text-gray-600 text-sm font-bold mb-1"
            >
              Start Date
            </label>
            <input
              type="date"
              name="userSuccessorStartDate"
              value={formData.userSuccessorStartDate}
              onChange={handleChange}
              className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* 4. Legacy - Successor - End Date */}
          <div id="successorEndDate" className="bg-gray-100 rounded p-1 m-1 w-full">
            <label
              htmlFor="userSuccessorEndDate"
              className="block text-gray-600 text-sm font-bold mb-1"
            >
              End Date
            </label>
            <input
              type="date"
              name="userSuccessorEndDate"
              value={formData.userSuccessorEndDate}
              onChange={handleChange}
              className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        {/* 5. Legacy - Successor -  Notes */}
        <div id="successorNotes" className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Successor Notes</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={() => {
                setFormData((prevState) => ({
                  ...prevState,
                  userSuccessorNotes: [
                    ...prevState.userSuccessorNotes,
                    {
                      successorNoteText: "",
                      successorNoteDate: new Date().toISOString(),
                    },
                  ],
                }));
              }}
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {formData.userSuccessorNotes.map((note, index) => (
            <div key={index} className="p-2 border rounded mb-2 bg-white">
              <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-xs">Note {index + 1}</p>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                  onClick={() => {
                    setFormData((prevState) => ({
                      ...prevState,
                      userSuccessorNotes: prevState.userSuccessorNotes.filter(
                        (_, idx) => idx !== index
                      ),
                    }));
                  }}
                >
                  <MinusIcon className="h-4 w-4 inline" />
                </button>
              </div>

              {/* Note Text */}
              <div className="bg-gray-100 rounded p-1 m-1">
                <label
                  htmlFor={`successorNoteText-${index}`}
                  className="block text-gray-600 text-sm font-bold mb-1"
                >
                  Note Text
                </label>
                <EtContentEditorLight2
                  value={note.successorNoteText || ""}
                  handleChange={(content) => {
                    setFormData((prevState) => ({
                      ...prevState,
                      userSuccessorNotes: prevState.userSuccessorNotes.map(
                        (n, idx) =>
                          idx === index
                            ? { ...n, successorNoteText: content }
                            : n
                      ),
                    }));
                  }}
                  placeholderText="Write your note here..."
                  editorKey={`successorNoteText-${index}`}
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* 6. Form Submissions Bar */}
        <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic 
              name={'Create'} 
              type={'create'} 
              handleClick={handleSubmit} 
              />
            <EtBtnFormBasic
              name={'Clear'}
              type={'clear'}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={'Cancel'}
              type={'cancel'}
              handleClick={handleCancel}
            />
        </div>

      </form>


    </div>
  )
}

export default EtCreateUserImprintLegacySuccessorComp